import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { Dropdown, Button } from 'antd'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { clearError } from '../../../features/auth/apis/authSlice'
import { setNavbarHeight } from '../../../redux/themeSlice'
import { isDashboardAuthIntent } from '../../../utils/authRedirect'
import { webAssets } from '../../../utils/webAssets'

import NavNotification from '../NavNotification'
import NavProfile from '../NavProfile'
import NavLanguage from '../NavLanguage'
import Register from '../../../features/auth/components/Register'
import Login from '../../../features/auth/components/Login'
import NavToggler from '../../svgs/NavToggler'
import MobileSideNav from '../MobileSideNav'
import Chevron from '../../svgs/Chevron'
import Phone from '../../svgs/phone'
import {
  normalizeLayoutPath,
  shouldDisableLayoutElementClick,
  shouldHideLayoutElement,
} from './layoutElementVisibility'
import {
  PUBLIC_SUBSCRIPTION_BASE_PATH,
  publicSubscriptionRoutes,
} from '@/utils/subscriptionRoutes'
import { getPublicPackageRoute } from '@/utils/packageRoutes'
// import NavContact from '../NavContact'

const SUPPORT_PHONE_NUMBER = '0282944581'
const SUPPORT_PHONE_LINK = `tel:${SUPPORT_PHONE_NUMBER}`

const HomeNav = () => {
  const ref = useRef(null)
  const router = useRouter()
  const dispatch = useDispatch()
  const { t } = useTranslation('HomeLayout')
  const { dashboardTitle } = useSelector((state) => state.theme)

  const coversation_token = useMemo(
    () => router.query.coversation_token,
    [router.query.coversation_token]
  )

  const { profile } = useSelector((state) => state.auth)

  const [sideNavOpen, setSideNavOpen] = useState(false)
  const [registerOpen, setRegisterOpen] = useState(false)
  const [loginOpen, setLoginOpen] = useState(false)
  const currentPath = router.asPath
  const isLandingPage = normalizeLayoutPath(currentPath).startsWith('/lp')
  const subscriptionPromoPathname = publicSubscriptionRoutes.promo.replace(/^\//, '')

  const hideNavbarLogo = shouldHideLayoutElement('navbarLogo', currentPath)
  const hideNavbarLinks = shouldHideLayoutElement('navbarLinks', currentPath)
  const hideNavbarLanguage = shouldHideLayoutElement('navbarLanguage', currentPath)
  const hideNavbarSupportPhone = shouldHideLayoutElement(
    'navbarSupportPhone',
    currentPath
  )
  const hideNavbarAuthButtons = shouldHideLayoutElement(
    'navbarAuthButtons',
    currentPath
  )
  const disableNavbarLogoClick = shouldDisableLayoutElementClick(
    'navbarLogo',
    currentPath
  )

  // HANDLE IF COVERSATION TOKEN EXIST [TICKETS]
  useEffect(() => {
    if (coversation_token && !registerOpen) {
      localStorage.setItem('SMARTCAF_conversation_token', coversation_token)
      setRegisterOpen(true)
    }
  }, [setRegisterOpen, registerOpen, coversation_token])

  // HANDLE IF REDIRECTION LINK EXIST
  useEffect(() => {
    const redirectLink = Array.isArray(router.query['redirection_link'])
      ? router.query['redirection_link'][0]
      : router.query['redirection_link']

    if (isDashboardAuthIntent(redirectLink)) {
      setLoginOpen(true)
    }
  }, [router.query])

  const clearDashboardRedirectIntent = useCallback(() => {
    const redirectLink = Array.isArray(router.query['redirection_link'])
      ? router.query['redirection_link'][0]
      : router.query['redirection_link']

    if (!isDashboardAuthIntent(redirectLink)) {
      return
    }

    const nextQuery = { ...router.query }
    delete nextQuery.redirection_link

    router.replace(
      {
        pathname: router.pathname,
        query: nextQuery,
      },
      undefined,
      { shallow: true }
    )
  }, [router])

  const handleLoginDismiss = useCallback(() => {
    setLoginOpen(false)
    clearDashboardRedirectIntent()
  }, [clearDashboardRedirectIntent])

  const handleRegisterDismiss = useCallback(() => {
    setRegisterOpen(false)
    clearDashboardRedirectIntent()
  }, [clearDashboardRedirectIntent])

  // SETTING NAVBAR HEIGHT
  useEffect(() => {
    dispatch(setNavbarHeight(ref.current.clientHeight))
  })

  const navLinks = [
    {
      id: 0,
      title: 'Home',
      pathname: '',
    },
    // {
    //   id: 1,
    //   title: 'homenav.Services',
    //   pathname: 'services',
    // },
    {
      id: 1,
      title: 'homenav.Services',
      pathname: 'servizi',
    },
    {
      id: 2,
      title: 'Abbonamenti',
      pathname: subscriptionPromoPathname,
    },
    {
      id: 3,
      title: 'homenav.ContactUs',
      pathname: 'contact-us',
    },
    {
      id: 4,
      title: 'Blog',
      pathname: 'blog',
    },
    {
      id: 5,
      title: 'homenav.Branches',
      pathname: 'branches',
    },
  ]

  const abbonamentoItems = [
    {
      key: '1',
      onClick: () => router.push(publicSubscriptionRoutes.promo),
      label: (
        <div className='hover:text-black font-base font-semibold whitespace-nowrap'>
          CAF e Patronato
        </div>
      ),
    },
    {
      key: '2',
      onClick: () => router.push(getPublicPackageRoute('colf-e-badanti')),
      label: (
        <div className='hover:text-black font-base font-semibold whitespace-nowrap'>
          Colf e Badanti
        </div>
      ),
    },
    {
      key: '3',
      onClick: () => router.push(getPublicPackageRoute('smart-forfettario')),
      label: (
        <div className='hover:text-black font-base font-semibold whitespace-nowrap'>
          Smart Forfettario
        </div>
      ),
    },
  ]

  const normalizedPath = router.asPath.split('?')[0]
  const isAbbonamentoActive =
    normalizedPath.startsWith(PUBLIC_SUBSCRIPTION_BASE_PATH) ||
    [getPublicPackageRoute('colf-e-badanti'), getPublicPackageRoute('smart-forfettario')].includes(
      normalizedPath
    )

  return (
    <>
      <nav
        ref={ref}
        className='bg-white shadow-sm py-16 lg:py-20 sticky top-0 right-0 left-0 z-99'
      >
        <div className={`container mx-auto flex items-center ${isLandingPage ? 'justify-start' : 'justify-between'}`}>
          {isLandingPage ? (
            <Image
              src={webAssets.logoLight}
              alt='Logo SmartCAF'
              width={86}
              height={60}
              priority
              className='!h-[3.875rem] !w-[5.563rem]'
            />
          ) : (
            <>
          {/* LEFT */}
          <div className='flex items-center'>
            {/* NAV TOGGLER */}
            <NavToggler
              style='lg:hidden ltr:mr-8 rtl:ml-8 ltr:-scale-x-100 cursor-pointer'
              onClick={() => setSideNavOpen(true)}
            />
            <h1 className='lg:hidden text-base font-bold'>
              {t(dashboardTitle)}
            </h1>
            {/* LOGO */}
            {hideNavbarLogo ? null : (
              <Image
                src={webAssets.logoLight}
                alt='Logo SmartCAF'
                width={86}
                height={60}
                priority
                className={`hidden lg:block ltr:lg:mr-50 rtl:lg:ml-50 !w-[5.563rem] !h-[3.875rem] ${disableNavbarLogoClick ? '' : 'cursor-pointer'}`}
                onClick={
                  disableNavbarLogoClick
                    ? undefined
                    : () => router.push('/', undefined, { shallow: true })
                }
              />
            )}

            {/* NAVLINKS */}
            {hideNavbarLinks ? null : (
              <ul className='hidden lg:flex items-center mb-0'>
                {navLinks.map((ele) => {
                  if (ele.title === 'homenav.Services') {
                    return (
                      <li
                        key={ele.id}
                        className={`text-[#AAA] ltr:mr-50 ltr:last:mr-0 rtl:ml-50 rtl:last:ml-0 text-base font-medium hover:text-green transition-colors relative ${router.asPath.split('/')[1] === ele.pathname
                            ? 'text-green'
                            : ''
                          }`}
                      >
                        <Link href={`/${ele.pathname}`}>{t(ele.title)}</Link>
                      </li>
                    )
                  }

                  if (ele.title === 'Abbonamenti') {
                    return (
                      <Dropdown
                        key={ele.id}
                        menu={{
                          items: abbonamentoItems,
                        }}
                      >
                        <li
                          className={`text-[#AAA] ltr:mr-50 ltr:last:mr-0 rtl:ml-50 rtl:last:ml-0 text-base font-medium hover:text-green transition-colors relative ${isAbbonamentoActive ? 'text-green' : ''} flex items-center gap-8 cursor-pointer`}
                        >
                          <span>{t(ele.title)}</span>
                          <Chevron color='#AAA' />
                        </li>
                      </Dropdown>
                    )
                  }

                  return (
                    <li
                      key={ele.id}
                      className={`text-[#AAA] ltr:mr-50 ltr:last:mr-0 rtl:ml-50 rtl:last:ml-0 text-base font-medium hover:text-green transition-colors relative ${router.asPath.split('/')[1] === ele.pathname
                          ? 'text-green'
                          : ''
                        }`}
                    >
                      <Link href={`/${ele.pathname}`}>{t(ele.title)}</Link>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>

          {/* RIGHT */}
          <div className='flex items-center'>
            {hideNavbarLanguage ? null : <NavLanguage />}

            {profile ? (
              <>
                {hideNavbarSupportPhone ? null : (
                  <a
                    className='flex items-center justify-center bg-green_150 rounded-lg w-[40px] h-[40px] ltr:ml-8 ltr:mr-20 lg:ltr:mr-24 sm:ltr:mr-40 rtl:ml-40 rtl:mr-8 cursor-pointer'
                    href={SUPPORT_PHONE_LINK}
                    aria-label='Chiama Assistenza SmartCAF'
                  >
                    <Phone width={20} height={20} />
                  </a>
                )}
                <NavNotification />
                <NavProfile />
              </>
            ) : (
              <div className='flex items-center ltr:ml-24 rtl:mr-24'>
                {hideNavbarSupportPhone ? null : (
                  <a
                    className='flex items-center justify-center bg-green_150 rounded-lg w-[40px] h-[40px] ltr:mr-12 rtl:ml-12'
                    href={SUPPORT_PHONE_LINK}
                    aria-label='Chiama Assistenza SmartCAF'
                  >
                    <Phone width={20} height={20} />
                  </a>
                )}
                {hideNavbarAuthButtons ? null : (
                  <>
                    <Button
                      className='hidden lg:inline btn-green h-auto w-128 text-sm font-medium rounded-lg py-16 ltr:mr-20 rtl:ml-20'
                      onClick={() => {
                        dispatch(clearError())
                        setLoginOpen(true)
                      }}
                    >
                      {t('homenav.Login')}
                    </Button>
                    <Button
                      className='hidden lg:inline btn-green-outlined h-auto w-128 text-sm font-medium rounded-lg py-16'
                      onClick={() => setRegisterOpen(true)}
                    >
                      {t('homenav.Register')}
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* <NavContact /> */}
          </div>
            </>
          )}
        </div>
      </nav>

      {isLandingPage ? null : (
        <Register
          open={registerOpen}
          setOpen={setRegisterOpen}
          onDismiss={handleRegisterDismiss}
        />
      )}
      {isLandingPage ? null : (
        <Login
          open={loginOpen}
          setOpen={setLoginOpen}
          onDismiss={handleLoginDismiss}
        />
      )}
      {isLandingPage ? null : <MobileSideNav open={sideNavOpen} setOpen={setSideNavOpen} />}
    </>
  )
}

export default HomeNav
